import { useMemo, useState } from "react";
import { Box, Chip, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { OpenInNew, WarningAmber } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { CalendarSlot } from "../types";
import { generateDaysArray } from "../shared/calendarUtils";
import { DAYS_WINDOW, SATURDAY_WEEKDAY, SATURDAY_HIGHLIGHT_COLOR } from "../constants";
import { SlotsPopoverInformation } from "../shared/SlotsPopoverInformation";
import { ReservationRow } from "./ReservationRow";
import { SessionCellContent } from "./SessionCellContent";
import { ClientRow } from "./listTypes";
import { getShortName } from "./listHelpers";
import { calculateWeeksWithMismatch, processClientRows } from "./listUtils";

type MismatchedWeeksChipsProps = {
    client: ClientRow;
    showNfb: boolean;
};

const MismatchedWeeksChips = ({ client, showNfb }: MismatchedWeeksChipsProps) => {
    const nfbMismatchedWeeks = calculateWeeksWithMismatch(
        client.nfbSessionsByDate,
        client.nfbReservations.length,
        client.isWeeklyNfb
    );
    const psyMismatchedWeeks = calculateWeeksWithMismatch(
        client.psySessionsByDate,
        client.psyReservations.length,
        client.isWeeklyPsy
    );

    const hasNfbMismatches = showNfb && nfbMismatchedWeeks.length > 0;
    const hasPsyMismatches = psyMismatchedWeeks.length > 0;

    if (!hasNfbMismatches && !hasPsyMismatches) return null;

    const formatDiff = (diff: number) => {
        return diff > 0 ? `+${diff}` : `${diff}`;
    };

    return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
            {hasNfbMismatches && nfbMismatchedWeeks.map((week, index) => (
                <Chip
                    key={`nfb-${index}`}
                    label={`${week.start} - ${week.end} (${formatDiff(week.diff)})`}
                    size="small"
                    color="warning"
                    sx={{ fontSize: "0.65rem", height: 18 }}
                />
            ))}
            {hasPsyMismatches && psyMismatchedWeeks.map((week, index) => (
                <Chip
                    key={`psy-${index}`}
                    label={`${week.start} - ${week.end} (${formatDiff(week.diff)})`}
                    size="small"
                    color="primary"
                    sx={{ fontSize: "0.65rem", height: 18 }}
                />
            ))}
        </Box>
    );
};

type ReservedSlotsListProps = {
    startDate: Date;
    allSlots: Array<CalendarSlot>;
    showNfb: boolean;
    hasTherapistFilter: boolean;
    showOnlyMismatched: boolean;
};

export const ReservedSlotsList = ({ startDate, allSlots, showNfb, hasTherapistFilter, showOnlyMismatched }: ReservedSlotsListProps) => {
    const [warningAnchorEl, setWarningAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [selectedClient, setSelectedClient] = useState<ClientRow | null>(null);

    const days = useMemo(() => generateDaysArray(startDate, DAYS_WINDOW), [startDate]);
    const allClientRows = useMemo(() => processClientRows(allSlots, hasTherapistFilter), [allSlots, hasTherapistFilter]);

    const clientRows = useMemo(() => {
        if (!showOnlyMismatched) return allClientRows;

        return allClientRows.filter((client) => {
            const hasNfbMismatch = Object.values(client.nfbSessionsByDate).some((session) => !session.matches);
            const hasPsyMismatch = Object.values(client.psySessionsByDate).some((session) => !session.matches);
            const hasMissedSessions = client.missedNfb || client.missedPsy;
            const hasIssue = hasNfbMismatch || hasPsyMismatch || hasMissedSessions;

            return hasIssue;
        });
    }, [allClientRows, showOnlyMismatched]);

    const handleWarningClick = (event: React.MouseEvent<HTMLButtonElement>, client: ClientRow) => {
        event.stopPropagation();
        setWarningAnchorEl(event.currentTarget);
        setSelectedClient(client);
    };

    const handlePopoverClose = () => {
        setWarningAnchorEl(null);
        setSelectedClient(null);
    };

    const popoverClients = selectedClient
        ? [{ clientId: selectedClient.clientId, clientName: selectedClient.clientName, treatmentId: selectedClient.treatmentId, missedTwoConsecutiveSessions: true }]
        : [];

    if (clientRows.length === 0) {
        return (
            <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography color="text.secondary">Nenhum cliente encontrado.</Typography>
            </Box>
        );
    }

    return (
        <>
            <TableContainer sx={{ maxHeight: "calc(100vh - 200px)", overflow: "auto" }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell
                                sx={{
                                    position: "sticky",
                                    left: 0,
                                    zIndex: 3,
                                    backgroundColor: "background.paper",
                                    fontWeight: "bold",
                                }}
                            >
                                Cliente
                            </TableCell>
                            <TableCell
                                sx={{
                                    backgroundColor: "background.paper",
                                    fontWeight: "bold",
                                }}
                            >
                                Reservas
                            </TableCell>
                            <TableCell
                                sx={{
                                    backgroundColor: "background.paper",
                                    fontWeight: "bold",
                                    textAlign: "center",
                                    minWidth: 50,
                                }}
                            >
                                Falhou 2
                            </TableCell>
                            {days.map((day) => (
                                <TableCell
                                    key={day.dateKey}
                                    align="center"
                                    sx={{
                                        minWidth: 50,
                                        fontWeight: "bold",
                                        backgroundColor: day.weekDay === SATURDAY_WEEKDAY ? SATURDAY_HIGHLIGHT_COLOR : "background.paper",
                                        px: 0.5,
                                    }}
                                >
                                    <Typography variant="caption" display="block" fontWeight="bold" sx={{ fontSize: "0.75rem" }}>
                                        {day.weekDayLabel}
                                    </Typography>
                                    <Typography variant="caption" display="block" sx={{ fontSize: "0.75rem" }}>
                                        {format(day.date, "dd/MM")}
                                    </Typography>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {clientRows.map((client) => (
                            <TableRow key={`${client.clientId}_${client.treatmentId}`} hover>
                                <TableCell
                                    sx={{
                                        position: "sticky",
                                        left: 0,
                                        zIndex: 1,
                                        backgroundColor: "#fafafa",
                                        py: 0.5,
                                        px: 1,
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <Typography variant="body2" fontWeight="bold" sx={{ fontSize: "0.85rem" }}>
                                            {client.clientId} - {getShortName(client.clientName)}
                                        </Typography>
                                        <IconButton
                                            component={Link}
                                            to={`/treatments/${client.treatmentId}/sessions`}
                                            target="_blank"
                                            size="small"
                                            color="secondary"
                                            sx={{ p: 0.25 }}
                                        >
                                            <OpenInNew sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </Box>
                                    <MismatchedWeeksChips client={client} showNfb={showNfb} />
                                </TableCell>
                                <TableCell sx={{ backgroundColor: "#fafafa", p: 0 }}>
                                    {showNfb && (
                                        <Box sx={{ height: 28, display: "flex", alignItems: "center", px: 0.5, borderBottom: "1px solid #e0e0e0" }}>
                                            <ReservationRow label="NFB" reservations={client.nfbReservations} color="warning" />
                                        </Box>
                                    )}
                                    <Box sx={{ height: 28, display: "flex", alignItems: "center", px: 0.5 }}>
                                        <ReservationRow label="PSY" reservations={client.psyReservations} color="primary" />
                                    </Box>
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{ backgroundColor: "#fafafa", p: 0 }}
                                >
                                    {showNfb && (
                                        <Box sx={{ height: 28, display: "flex", alignItems: "center", justifyContent: "center", borderBottom: "1px solid #e0e0e0" }}>
                                            {client.missedNfb ? (
                                                <IconButton
                                                    onClick={(e) => handleWarningClick(e, client)}
                                                    sx={{ p: 0.25 }}
                                                    size="small"
                                                    title="NFB - Faltou 2 sessões"
                                                >
                                                    <WarningAmber sx={{ fontSize: 20, color: "#ed6c02" }} />
                                                </IconButton>
                                            ) : (
                                                <Typography variant="caption" color="text.disabled">-</Typography>
                                            )}
                                        </Box>
                                    )}
                                    <Box sx={{ height: 28, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        {client.missedPsy ? (
                                            <IconButton
                                                onClick={(e) => handleWarningClick(e, client)}
                                                sx={{ p: 0.25 }}
                                                size="small"
                                                title="PSY - Faltou 2 sessões"
                                            >
                                                <WarningAmber sx={{ fontSize: 20, color: "#1976d2" }} />
                                            </IconButton>
                                        ) : (
                                            <Typography variant="caption" color="text.disabled">-</Typography>
                                        )}
                                    </Box>
                                </TableCell>
                                {days.map((day) => {
                                    const nfbSession = client.nfbSessionsByDate[day.dateKey];
                                    const psySession = client.psySessionsByDate[day.dateKey];

                                    return (
                                        <TableCell
                                            key={day.dateKey}
                                            align="center"
                                            sx={{ p: 0 }}
                                        >
                                            <SessionCellContent
                                                nfbSession={nfbSession}
                                                psySession={psySession}
                                                showNfb={showNfb}
                                            />
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <SlotsPopoverInformation
                anchorEl={warningAnchorEl}
                onClose={handlePopoverClose}
                reservations={popoverClients}
                bookings={[]}
            />
        </>
    );
};

